import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Plus, X, Download, CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadCsv } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import AllergenChips from "@/components/AllergenChips";
import AIPanel from "@/components/AIPanel";
import ShelfLifeTrend from "@/components/ShelfLifeTrend";
import AttachmentsCard from "@/components/AttachmentsCard";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import { ExperimentForm } from "@/pages/Experiments";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  fmtDate, fmtDateTime, label, PANEL_TYPES, FEEDBACK_SOURCES, FEEDBACK_TYPES,
  STORAGE_CONDITIONS, DEFAULT_CHECKPOINTS,
} from "@/lib/constants";

const TABS = ["design", "sensory", "stability", "feedback", "files"];

const SCORE_FIELDS = [
  ["appearance_score", "Appearance"], ["aroma_score", "Aroma"], ["texture_score", "Texture"],
  ["taste_score", "Taste"], ["aftertaste_score", "Aftertaste"], ["overall_liking_score", "Overall liking"],
];

export default function ExperimentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const confirm = useConfirm();

  const queryClient = useQueryClient();
  const [tab, setTab] = useState("design");
  const [modal, setModal] = useState(null); // 'edit' | 'sensory' | 'stability' | 'feedback'

  const { data, isLoading } = useQuery({
    queryKey: ["experiment", id],
    queryFn: async () => (await api.get(`/experiments/${id}/full`)).data,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["experiment", id] });

  const deleteExperiment = async () => {
    const ok = await confirm({
      title: "Delete experiment",
      message: "Delete this experiment and all its sensory results, stability records, checkpoints and feedback? This cannot be undone.",
      confirmLabel: "Delete experiment",
    });
    if (!ok) return;
    try {
      await api.delete(`/experiments/${id}`);
      toast.success("Experiment deleted");
      navigate("/experiments");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (isLoading || !data) return <div className="text-[#8C7A6B] text-sm" data-testid="experiment-detail-loading">Loading experiment…</div>;
  const { experiment: exp, formula, sensory, stability, feedback, checkpoints = [], ai_summary: aiSummary } = data;

  const avg = {};
  for (const [key] of SCORE_FIELDS) {
    avg[key] = sensory.length ? (sensory.reduce((s, r) => s + (r[key] || 0), 0) / sensory.length).toFixed(1) : null;
  }

  return (
    <div data-testid="experiment-detail-page">
      <Link to="/experiments" className="inline-flex items-center gap-2 text-sm text-[#8C7A6B] hover:text-[#2A1810] mb-4" data-testid="back-to-experiments">
        <ArrowLeft size={15} /> Back to experiments
      </Link>

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6 pb-4 border-b border-[#E8DFD1]">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl font-bold text-[#1C100B]" data-testid="experiment-name">{exp.name}</h1>
            <StatusBadge value={exp.status} testid="experiment-status" />
            <StatusBadge value={exp.location_type} testid="experiment-location" />
          </div>
          <p className="text-sm text-[#8C7A6B] mt-2">
            Formula: <span className="text-[#2A1810] font-medium">{formula?.name || "—"}</span>
            {formula?.version && <span className="text-[#8C7A6B]"> (v{formula.version})</span>}
            {"  ·  "}Owner: <span className="text-[#2A1810] font-medium">{exp.created_by_name}</span>
          </p>
          <div className="flex items-center gap-2 mt-2" data-testid="experiment-allergen-rollup">
            <span className="text-xs font-semibold tracking-wider uppercase text-[#8C7A6B]">Allergens</span>
            <AllergenChips allergens={exp.allergens} empty="none declared" />
          </div>
          <div className="text-xs text-[#8C7A6B] mt-2" data-testid="experiment-last-edited">
            Last edited by <span className="text-[#2A1810] font-medium">{exp.updated_by_name || "—"}</span> on{" "}
            <span className="text-[#2A1810] font-medium">{fmtDateTime(exp.updated_at)}</span>
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setModal("edit")} className={btnSecondary} data-testid="edit-experiment-button"><Pencil size={14} /> Edit</button>
            <button onClick={deleteExperiment} className="bg-[#FEF2F2] hover:bg-[#FEE2E2] text-[#B91C1C] border border-[#FECACA] px-4 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2" data-testid="delete-experiment-button">
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-2 border-b border-[#E8DFD1] mb-6" data-testid="experiment-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${
              tab === t
                ? "border-[#C5A059] text-[#2A1810] font-bold bg-[#FAF6F0]/50 rounded-t-lg"
                : "border-transparent text-[#8C7A6B] hover:text-[#2A1810]"
            }`}
          >
            {label(t)}
          </button>
        ))}
      </div>

      {tab === "design" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="tab-content-design">
          <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm">
            <h2 className="text-xs font-semibold tracking-wider uppercase text-[#8C7A6B] mb-2">Objective</h2>
            <p className="text-sm text-[#5C4A3E] leading-relaxed" data-testid="experiment-objective">{exp.objective || "—"}</p>
          </div>
          <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm">
            <h2 className="text-xs font-semibold tracking-wider uppercase text-[#8C7A6B] mb-2">Hypothesis</h2>
            <p className="text-sm text-[#5C4A3E] leading-relaxed" data-testid="experiment-hypothesis">{exp.hypothesis || "—"}</p>
          </div>
          <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm lg:col-span-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              <div><div className="text-xs font-semibold uppercase tracking-wider text-[#8C7A6B] mb-1">Location</div><div className="text-[#2A1810] font-medium">{label(exp.location_type)}</div></div>
              <div><div className="text-xs font-semibold uppercase tracking-wider text-[#8C7A6B] mb-1">Start</div><div className="text-[#2A1810] font-medium">{fmtDate(exp.start_date)}</div></div>
              <div><div className="text-xs font-semibold uppercase tracking-wider text-[#8C7A6B] mb-1">End</div><div className="text-[#2A1810] font-medium">{fmtDate(exp.end_date)}</div></div>
              <div><div className="text-xs font-semibold uppercase tracking-wider text-[#8C7A6B] mb-1">Last updated</div><div className="text-[#2A1810] font-medium">{fmtDate(exp.updated_at)}</div></div>
            </div>
          </div>
        </div>
      )}

      {tab === "sensory" && (
        <div className="space-y-6" data-testid="tab-content-sensory">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {SCORE_FIELDS.map(([key, lbl]) => (
              <div key={key} className="bg-white border border-[#E8DFD1] rounded-xl p-4 shadow-sm" data-testid={`sensory-avg-${key}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8C7A6B]">{lbl}</div>
                <div className="mt-1 font-display text-2xl font-bold text-[#2A1810]">{avg[key] ?? "—"}</div>
                {avg[key] && (
                  <div className="mt-2 h-1.5 bg-[#F4EFEA] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C5A059] rounded-full" style={{ width: `${(avg[key] / 9) * 100}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F4EFEA]">
              <h2 className="font-display text-lg font-semibold text-[#1C100B]">Panel Results ({sensory.length})</h2>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      await downloadCsv("/sensory-results/export.csv", "smoor-sensory-results.csv", { experiment_id: id });
                      toast.success("Sensory results exported");
                    } catch (err) {
                      toast.error(apiError(err));
                    }
                  }}
                  className={`${btnSecondary} !py-2`}
                  data-testid="export-sensory-button"
                >
                  <Download size={14} className="inline mr-1.5 -mt-0.5" /> Export CSV
                </button>
                {canWrite && (
                  <button onClick={() => setModal("sensory")} className={`${btnSecondary} !py-2`} data-testid="add-sensory-button">
                    <Plus size={14} className="inline mr-1.5 -mt-0.5" /> Add result
                  </button>
                )}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Panel</th>
                  <th className="px-4 py-3 text-left font-semibold">Segment</th>
                  <th className="px-4 py-3 text-right font-semibold">Taste</th>
                  <th className="px-4 py-3 text-right font-semibold">Liking</th>
                  <th className="px-4 py-3 text-left font-semibold">Comments</th>
                  <th className="px-4 py-3 text-left font-semibold">Recorded</th>
                  {canWrite && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {sensory.map((s) => (
                  <tr key={s.id} className="border-b border-[#F4EFEA] last:border-0" data-testid={`sensory-row-${s.id}`}>
                    <td className="px-4 py-3 text-[#2A1810] font-medium">{label(s.panel_type)}</td>
                    <td className="px-4 py-3 text-[#5C4A3E]">{label(s.respondent_segment)}</td>
                    <td className="px-4 py-3 text-right text-[#5C4A3E]">{s.taste_score}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#B08B46]">{s.overall_liking_score}</td>
                    <td className="px-4 py-3 text-[#5C4A3E] max-w-xs truncate">{s.comments}</td>
                    <td className="px-4 py-3 text-[#8C7A6B]">{fmtDate(s.recorded_at)}</td>
                    {canWrite && (
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={async () => { await api.delete(`/sensory-results/${s.id}`); toast.success("Result deleted"); refresh(); }}
                          className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#B91C1C]"
                          data-testid={`delete-sensory-${s.id}`}
                        >
                          <X size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {sensory.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8C7A6B]">No sensory results yet.</td></tr>}
              </tbody>
            </table>
          </div>

          <AIPanel
            title="AI Sensory Summary"
            description="Structured read of this experiment's panel scores and experience-centre feedback, ending in an iterate / proceed / stop call."
            endpoint={`/ai/experiments/${id}/summary`}
            cacheKey={`experiment-${id}`}
            initial={aiSummary}
            onGenerated={refresh}
            testid="ai-sensory-summary"
          />
        </div>
      )}

      {tab === "stability" && (
        <div className="space-y-6" data-testid="tab-content-stability">
          <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="stability-schedule-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F4EFEA]">
              <div className="flex items-center gap-2">
                <CalendarClock size={17} className="text-[#C5A059]" />
                <h2 className="font-display text-lg font-semibold text-[#1C100B]">Planned Checkpoints ({checkpoints.length})</h2>
              </div>
              {canWrite && (
                <button onClick={() => setModal("schedule")} className={`${btnSecondary} !py-2`} data-testid="schedule-stability-button">
                  <Plus size={14} className="inline mr-1.5 -mt-0.5" /> Schedule series
                </button>
              )}
            </div>
            <div className="px-6 py-4">
              {checkpoints.length === 0 ? (
                <p className="text-sm text-[#8C7A6B]" data-testid="checkpoints-empty">
                  No checkpoints planned. Schedule a series (e.g. 7 / 30 / 60 / 90 days) to track due dates from the experiment start date.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="checkpoints-list">
                  {checkpoints.map((c) => (
                    <span
                      key={c.id}
                      data-testid={`checkpoint-${c.id}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                        c.completed
                          ? "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]"
                          : "bg-[#F9F3E6] text-[#8A6D2F] border-[#E5C992]"
                      }`}
                    >
                      {c.completed && <CheckCircle2 size={12} />}
                      Day {c.day}
                      {c.storage_condition ? ` · ${c.storage_condition}` : ""}
                      {canWrite && (
                        <button
                          onClick={async () => {
                            try {
                              await api.delete(`/stability-tests/schedule/${c.id}`);
                              toast.success("Checkpoint removed");
                              refresh();
                            } catch (err) {
                              toast.error(apiError(err));
                            }
                          }}
                          className="ml-1 hover:text-[#B91C1C]"
                          data-testid={`delete-checkpoint-${c.id}`}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="stability-tests-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F4EFEA]">
              <h2 className="font-display text-lg font-semibold text-[#1C100B]">Stability Tests ({stability.length})</h2>
              {canWrite && (
                <button onClick={() => setModal("stability")} className={`${btnSecondary} !py-2`} data-testid="add-stability-button">
                  <Plus size={14} className="inline mr-1.5 -mt-0.5" /> Add record
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Day</th>
                <th className="px-4 py-3 text-left font-semibold">Storage condition</th>
                <th className="px-4 py-3 text-right font-semibold">Quality</th>
                <th className="px-4 py-3 text-left font-semibold">Observations</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Recorded</th>
                {canWrite && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {stability.map((s) => (
                <tr key={s.id} className="border-b border-[#F4EFEA] last:border-0" data-testid={`stability-row-${s.id}`}>
                  <td className="px-4 py-3 font-bold text-[#2A1810]">D{s.time_point_days}</td>
                  <td className="px-4 py-3 text-[#5C4A3E]">{s.storage_condition}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#2A1810]" data-testid={`stability-quality-${s.id}`}>
                    {s.quality_score == null ? <span className="text-[#8C7A6B] font-normal">—</span> : `${s.quality_score}/10`}
                  </td>
                  <td className="px-4 py-3 text-[#5C4A3E] max-w-md">{s.observations}</td>
                  <td className="px-4 py-3"><StatusBadge value={s.result_status} /></td>
                  <td className="px-4 py-3 text-[#8C7A6B]">{fmtDate(s.recorded_at)}</td>
                  {canWrite && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={async () => { await api.delete(`/stability-tests/${s.id}`); toast.success("Record deleted"); refresh(); }}
                        className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#B91C1C]"
                        data-testid={`delete-stability-${s.id}`}
                      >
                        <X size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {stability.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8C7A6B]">No stability records yet.</td></tr>}
            </tbody>
          </table>
            </div>
          </div>

          <ShelfLifeTrend experimentId={id} />
        </div>
      )}

      {tab === "feedback" && (
        <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm" data-testid="tab-content-feedback">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#F4EFEA]">
            <h2 className="font-display text-lg font-semibold text-[#1C100B]">Feedback ({feedback.length})</h2>
            {canWrite && (
              <button onClick={() => setModal("feedback")} className={`${btnSecondary} !py-2`} data-testid="add-feedback-button">
                <Plus size={14} /> Add feedback
              </button>
            )}
          </div>
          <div className="divide-y divide-[#F4EFEA]">
            {feedback.map((f) => (
              <div key={f.id} className="px-6 py-4" data-testid={`feedback-row-${f.id}`}>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <StatusBadge value={f.source} />
                  <StatusBadge value={f.feedback_type} />
                  {f.rating != null && <span className="text-xs font-bold text-[#B08B46]">★ {f.rating}/5</span>}
                  <span className="text-xs text-[#8C7A6B] ml-auto">{fmtDate(f.recorded_at)}</span>
                  {canWrite && (
                    <button
                      onClick={async () => { await api.delete(`/feedback/${f.id}`); toast.success("Feedback deleted"); refresh(); }}
                      className="p-1 rounded hover:bg-[#FEF2F2] text-[#B91C1C]"
                      data-testid={`delete-feedback-${f.id}`}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
                <p className="text-sm text-[#5C4A3E] leading-relaxed">{f.comment}</p>
                {f.tags?.length > 0 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {f.tags.map((t) => (
                      <span key={t} className="text-[10px] font-semibold bg-[#F9F3E6] text-[#8A6D2F] border border-[#E5C992] px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {feedback.length === 0 && <div className="px-6 py-8 text-center text-[#8C7A6B] text-sm">No feedback recorded for this experiment.</div>}
          </div>
        </div>
      )}

      {tab === "files" && (
        <div data-testid="tab-content-files">
          <AttachmentsCard
            basePath={`/experiments/${id}`}
            scope="experiment"
            emptyText="No files attached. Add trial photos, lab reports or supplier COAs for this experiment."
          />
        </div>
      )}

      {modal === "edit" && <ExperimentForm initial={exp} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === "sensory" && <SensoryForm experimentId={id} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === "stability" && <StabilityForm experimentId={id} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === "schedule" && <ScheduleForm experimentId={id} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === "feedback" && <FeedbackForm experimentId={id} onClose={() => setModal(null)} onSaved={refresh} />}
    </div>
  );
}

function SensoryForm({ experimentId, onClose, onSaved }) {
  const [form, setForm] = useState({
    panel_type: "trained_panel", respondent_segment: "", appearance_score: 7, aroma_score: 7,
    texture_score: 7, taste_score: 7, aftertaste_score: 7, overall_liking_score: 7, comments: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, experiment_id: experimentId };
      for (const [k] of SCORE_FIELDS) payload[k] = Number(payload[k]);
      await api.post("/sensory-results", payload);
      toast.success("Sensory result added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Modal title="Add Sensory Result" onClose={onClose} testid="sensory-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="sensory-form">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Panel type">
            <select data-testid="sensory-panel-select" value={form.panel_type} onChange={set("panel_type")} className={inputCls}>
              {PANEL_TYPES.map((p) => <option key={p} value={p}>{label(p)}</option>)}
            </select>
          </Field>
          <Field label="Respondent segment">
            <input data-testid="sensory-segment-input" value={form.respondent_segment} onChange={set("respondent_segment")} className={inputCls} placeholder="e.g. age_25_40" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {SCORE_FIELDS.map(([key, lbl]) => (
            <Field key={key} label={lbl}>
              <input data-testid={`sensory-${key}-input`} type="number" step="0.1" min={0} max={9} value={form[key]} onChange={set(key)} className={inputCls} />
            </Field>
          ))}
        </div>
        <Field label="Comments">
          <textarea data-testid="sensory-comments-input" rows={2} value={form.comments} onChange={set("comments")} className={inputCls} />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="sensory-cancel-button">Cancel</button>
          <button type="submit" className={btnPrimary} data-testid="sensory-save-button">Save result</button>
        </div>
      </form>
    </Modal>
  );
}

function StabilityForm({ experimentId, onClose, onSaved }) {
  const [form, setForm] = useState({ time_point_days: 7, storage_condition: "25C / 60% RH", observations: "", result_status: "ok", quality_score: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stability-tests", {
        ...form,
        experiment_id: experimentId,
        time_point_days: Number(form.time_point_days),
        quality_score: form.quality_score === "" ? null : Number(form.quality_score),
      });
      toast.success("Stability record added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Modal title="Add Stability Record" onClose={onClose} testid="stability-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="stability-form">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Time point (days)">
            <input data-testid="stability-days-input" type="number" min={0} value={form.time_point_days} onChange={set("time_point_days")} className={inputCls} />
          </Field>
          <Field label="Result status">
            <select data-testid="stability-status-select" value={form.result_status} onChange={set("result_status")} className={inputCls}>
              <option value="ok">OK</option>
              <option value="issue">Issue</option>
            </select>
          </Field>
        </div>
        <Field label="Storage condition">
          <select data-testid="stability-condition-input" value={form.storage_condition} onChange={set("storage_condition")} className={inputCls}>
            {STORAGE_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Quality score 0–10 (optional — drives the shelf-life trend)">
          <input
            data-testid="stability-quality-input"
            type="number"
            step="0.1"
            min={0}
            max={10}
            value={form.quality_score}
            onChange={set("quality_score")}
            className={inputCls}
            placeholder="e.g. 8.5 — bloom, texture and aroma holding up"
          />
        </Field>
        <Field label="Observations">
          <textarea data-testid="stability-observations-input" rows={3} value={form.observations} onChange={set("observations")} className={inputCls} />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="stability-cancel-button">Cancel</button>
          <button type="submit" className={btnPrimary} data-testid="stability-save-button">Save record</button>
        </div>
      </form>
    </Modal>
  );
}

function ScheduleForm({ experimentId, onClose, onSaved }) {
  const [days, setDays] = useState(DEFAULT_CHECKPOINTS.join(", "));
  const [condition, setCondition] = useState(STORAGE_CONDITIONS[1]);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const checkpoints = days.split(",").map((d) => Number(d.trim())).filter((d) => !Number.isNaN(d) && d >= 0);
    if (!checkpoints.length) {
      toast.error("Enter at least one checkpoint day");
      return;
    }
    setSaving(true);
    try {
      await api.post("/stability-tests/schedule", {
        experiment_id: experimentId,
        checkpoints,
        storage_condition: condition,
      });
      toast.success("Checkpoint series scheduled");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Schedule Stability Series" onClose={onClose} testid="schedule-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="schedule-form">
        <Field label="Checkpoint days (comma separated)">
          <input data-testid="schedule-days-input" value={days} onChange={(e) => setDays(e.target.value)} className={inputCls} placeholder="7, 30, 60, 90" />
        </Field>
        <Field label="Storage condition">
          <select data-testid="schedule-condition-select" value={condition} onChange={(e) => setCondition(e.target.value)} className={inputCls}>
            {STORAGE_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <p className="text-xs text-[#8C7A6B]">
          Due dates are computed from the experiment start date. Checkpoints due within 7 days or overdue appear on the Dashboard.
        </p>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="schedule-cancel-button">Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="schedule-save-button">
            {saving ? "Scheduling…" : "Schedule series"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function FeedbackForm({ experimentId, onClose, onSaved }) {
  const [form, setForm] = useState({ source: "experience_centre", feedback_type: "rating", rating: 5, comment: "", tags: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/feedback", {
        experiment_id: experimentId,
        source: form.source,
        feedback_type: form.feedback_type,
        rating: form.rating === "" ? null : Math.round(Number(form.rating)),
        comment: form.comment,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Feedback added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Modal title="Add Feedback" onClose={onClose} testid="feedback-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Source">
            <select data-testid="feedback-source-select" value={form.source} onChange={set("source")} className={inputCls}>
              {FEEDBACK_SOURCES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <select data-testid="feedback-type-select" value={form.feedback_type} onChange={set("feedback_type")} className={inputCls}>
              {FEEDBACK_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
          </Field>
          <Field label="Rating (1-5)">
            <input data-testid="feedback-rating-input" type="number" step="1" min={1} max={5} value={form.rating} onChange={set("rating")} className={inputCls} />
          </Field>
        </div>
        <Field label="Comment">
          <textarea data-testid="feedback-comment-input" rows={3} required value={form.comment} onChange={set("comment")} className={inputCls} />
        </Field>
        <Field label="Tags (comma separated)">
          <input data-testid="feedback-tags-input" value={form.tags} onChange={set("tags")} className={inputCls} placeholder="e.g. too sweet, silky texture" />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="feedback-cancel-button">Cancel</button>
          <button type="submit" className={btnPrimary} data-testid="feedback-save-button">Save feedback</button>
        </div>
      </form>
    </Modal>
  );
}
